// DO NOT ADD ANY NEW IMPORTS HERE.
import { ElementOf, SelectFromUnion } from '@fullstory/typeutils';
import { AttributeBlockEntryForApi, V2OperationOptions } from '@fullstory/browser-public-api';

// NOTE: These constants, structs, and functions must match those in rawevents.proto

export namespace RawEventKind {
  // Obsolete event. Insertions recorded before /start responds.
  // Args: [parentId, beforeId, Node]
  // [DO NOT USE] MUT_UNPARSED_INSERT = 0;

  // Obsolete event. Insertions recorded internally, but not yet saved (may be skipped).
  // Args: [id, [parents], parentId, beforeId, stream...] (only used during recording)
  // [DO NOT USE] MUT_PARSED_INSERT = 1;

  export const MUT_INSERT = 2;
  export const MUT_REMOVE = 3;
  /**
   * MUT_ATTR event contains attribute changes (add, remove, updates) to captured elements
   * as reported by a MutationObserver.
   *
   * Privacy: attribute privacy is controlled via OrgSettings.AttributeBlocklist
   * where each entry defines the tag, attributes, and action to take (ERASE, MASKTEXT, SCRUBURL, SCRUBCSS).
   * Attribute changes are monitored via MutationObserver and privacy is enforced via @see{scrub.ts#scrubAttrValue}.
   *
   * Special considerations:
   * - Links: prefetch links (<link rel="prefetch|preload|prerender" />) are monitored for changes
   *   and the elements are fully refreshed (e.g. re-encoded).
   *
   * - Privacy Rules: when an attribute changes the elements matching privacy rules could change, we compare
   *   the old and new element for privacy WatchKind and only if the exclusion becomes stricter we
   *   refresh the element. If less strict we track the attribute change only.
   *
   * - Observable Rules: when at attribute changes the element matching observable state could change (watched or keep rules)
   *   If the element is observable as a result of this attribute change we track the element in our ResizeWatcher
   *   framework to detect element size and when it's visible in the viewport.
   */
  export const MUT_ATTR = 4;

  // Obsolete (or at least unimplemented) event.
  // Args: [endTime, id, attr, hermite(value)]
  // [DO NOT USE] MUT_ATTR_CURVE = 5,

  export const MUT_TEXT = 6;

  // Obsolete event. Args: [count]
  // [DO NOT USE] MUT_SKIPIDS = 7,

  export const MOUSEMOVE = 8;
  export const MOUSEMOVE_CURVE = 9;

  export const SCROLL_LAYOUT = 10;
  export const SCROLL_LAYOUT_CURVE = 11;

  export const MOUSEDOWN = 12;
  export const MOUSEUP = 13;

  // Obsolete events.
  // [DO NOT USE] export const KEYDOWN = 14;
  // [DO NOT USE] export const KEYUP = 15;

  export const CLICK = 16;
  export const FOCUS = 17;
  export const VALUECHANGE = 18;
  export const RESIZE_LAYOUT = 19;
  export const DOMLOADED = 20;
  export const LOAD = 21;

  export const PLACEHOLDER_SIZE = 22;

  export const UNLOAD = 23;

  export const BLUR = 24;
  export const SET_FRAME_BASE = 25;

  // Whoops: Skipped 26-31 by accident.

  export const TOUCHSTART = 32;
  export const TOUCHEND = 33;
  export const TOUCHCANCEL = 34;
  export const TOUCHMOVE = 35;
  export const TOUCHMOVE_CURVE = 36;

  export const NAVIGATE = 37;

  export const PLAY = 38;
  export const PAUSE = 39;

  export const RESIZE_VISUAL = 40;
  export const RESIZE_VISUAL_CURVE = 41;

  export const RESIZE_DOCUMENT_CONTENT = 42;
  export const RESIZE_SCROLLABLE_ELEMENT_CONTENT = 43;

  // Whoops: Skipped 44-47 by accident.
  export const LOG = 48;
  export const ERROR = 49;

  export const DBL_CLICK = 50;
  export const FORM_SUBMIT = 51;

  export const WINDOW_FOCUS = 52;
  export const WINDOW_BLUR = 53;

  export const HEARTBEAT = 54;

  // Obsolete event. Deprecated in favor of RawEventKind.PERF_ENTRY/timing.
  // Args: [args:number[]]
  // [DO NOT USE] NAVIGATION_TIMING = 55,

  export const WATCHED_ELEM = 56;

  export const PERF_ENTRY = 57;

  export const REC_FEAT_SUPPORTED = 58;

  export const SELECT = 59;

  export const CSSRULE_INSERT = 60;
  export const CSSRULE_DELETE = 61;

  export const FAIL_THROTTLED = 62;

  export const AJAX_REQUEST = 63;

  export const SCROLL_VISUAL_OFFSET = 64;
  export const SCROLL_VISUAL_OFFSET_CURVE = 65;

  export const MEDIA_QUERY_CHANGE = 66;

  export const RESOURCE_TIMING_BUFFER_FULL = 67;

  export const MUT_SHADOW = 68;

  export const DISABLE_STYLESHEET = 69;

  export const FULLSCREEN = 70;
  export const FULLSCREEN_ERROR = 71;

  export const ADOPTED_STYLESHEETS = 72;
  export const CUSTOM_ELEMENT_DEFINED = 73;

  export const MODAL_OPEN = 74;
  export const MODAL_CLOSE = 75;

  // [DO NOT USE] SLOW_INTERACTION = 76; // was never used
  export const LONG_FRAME = 77;

  export const TIMING = 78;
  export const STORAGE_WRITE_FAILURE = 79;
  export const DOCUMENT_PROPERTIES = 80;
  export const ENTRY_NAVIGATE = 81;

  export const STATS = 82;

  /**
   * The parts of IntersectionObserverEntry we care about. These events
   * are only sent for watched elements.
   */
  export const VIEWPORT_INTERSECTION = 83;

  export const COPY = 84;
  export const PASTE = 85;

  export const URL_SALT = 86; // kitchen staff know what's up
  export const URL_ID = 87;

  export const FRAME_STATUS = 88;

  export const SCRIPT_COMPILED_VERSION = 89;

  // resets the css stylesheet
  export const RESET_CSS_SHEET = 90;

  // Animation Events
  export const ANIMATION_CREATED = 91;
  export const ANIMATION_METHOD_CALLED = 92;
  export const ANIMATION_PROPERTY_SET = 93;

  // NOTE: this event isn't used yet, but will be when we support custom AnimationTimelines
  export const DOCUMENT_TIMELINE_CREATED = 94;

  export const KEYFRAME_EFFECT_CREATED = 95;
  export const KEYFRAME_EFFECT_METHOD_CALLED = 96;
  export const KEYFRAME_EFFECT_PROPERTY_SET = 97;

  export const CAPTURE_SOURCE = 98;

  /** This is necessary for async page entity creation in recordstore for stateless capture */
  export const PAGE_DATA = 99;

  export const VISIBILITY_STATE = 100;

  export const DIALOG = 101;

  export const CSSRULE_UPDATE = 102;

  export const CANVAS = 103;
  export const CANVAS_DETACHED_DIMENSION = 104;

  /** Documents code-first rules added by the user */
  export const INIT_API = 105;

  /** Signals that a deferred node's children have been encoded */
  export const DEFERRED_RESOLVED = 106;

  export const ELEMENT_PROP = 109;

  export const BFCACHE_STATE = 110;

  /** Diagnostic information about the session lifecycle */
  export const SESSION_INFO = 111;

  export const EVENT_CANCELED = 112;

  /** General diagnostic information - internal use only */
  export const DIAGNOSTIC_INFO = 113;

  /** General resolvable event - allows an event to be resolved later at bundle time */
  export const RESOLVABLE_EVENT = 114;

  export const IN_SESSION_PREDICTION = 115;

  // 2xxx namespace for FS.Keep
  export const KEEP_ELEMENT = 2000;
  export const KEEP_URL = 2001;
  export const KEEP_BOUNCE = 2002;
  export const KEEP_CRASH = 2003;

  // Obsolete events. No longer used.
  // [DO NOT USE] COOKED_CLICK = 4096,       // [id]
  // [DO NOT USE] COOKED_FOCUS = 4097,       // [id]
  // [DO NOT USE] COOKED_CHANGE = 4098,      // [id, value]

  // Obsolete event; this has been replaced by SYS_SETVAR.
  // Args: [uid, metadata-json]
  // [DO NOT USE] SYS_SETMETA = 8192,

  export const SYS_SETVAR = 8193;

  // Obsolete event; this has been replaced by SYS_CUSTOM.
  // Args: [type, tag(, valuetype)]
  // [DO NOT USE] export const SYS_HIGHLIGHT = 8194,

  export const SYS_RESOURCEHASH = 8195;
  export const SYS_SETCONSENT = 8196;
  export const SYS_CUSTOM = 8197;
  export const SYS_REPORTCONSENT = 8198;
  export const SYS_LETHE_MOBILE_BUNDLE_SEQ = 8199;

  // reserved 1; // MUT_PARSED_INSERT
  // reserved 5; // MUT_ATTR_CURVE
  // reserved 7; // MUT_SKIPIDS
  // reserved 55; // NAVIGATION_TIMING
  // reserved 76; // SLOW_INTERACTION
  // reserved 107: // DEFERRED_MUT_INSERT
  // reserved 108: // DEFERRED_MUT_SHADOW
  // reserved 4096 to 4098; // COOKED_CLICK, COOKED_FOCUS, COOKED_CHANGE
  // reserved 8194; // SYS_HIGHLIGHT
}
export type RawEventName = keyof typeof RawEventKind;
export type RawEventKind = (typeof RawEventKind)[RawEventName];

export type RawTouchEventKind =
  | typeof RawEventKind.TOUCHSTART
  | typeof RawEventKind.TOUCHEND
  | typeof RawEventKind.TOUCHCANCEL
  | typeof RawEventKind.TOUCHMOVE
  | typeof RawEventKind.TOUCHMOVE_CURVE;

export type Hermite = [
  hermiteX0: number,
  hermiteX1: number,
  hermiteDX0: number,
  hermiteDX1: number,
];

// A node's FsId. This type exists to make the RawEvent types easier to read;
// it doesn't provide any type safety.
export type FsId = number;

// A timestamp in milliseconds. This type exists to make the RawEvent types
// easier to read; it doesn't provide any type safety.
export type TimeStampMs = number;

// HashAlgorithm used for resource uploading deduplication. These
// strings must match the keys of `hashAlgorithmResourceCacheField`
// in uploadresource.go.
export type HashAlgorithm = 'fsnv' | 'sha1';

// HTTP request made via XHR/fetch.
//
// Request and response headers are stored in the standard HTTP crlf-delimited
// format. Header names are lower-cased, to avoid confusion. HTTP header names
// are case-insensitive per spec. Headers without a trailing ':' have been
// redacted due to whitelisting.
//
// The request and response bodies are optional, and will be null if not recorded.
export type AjaxRequestArgs = [
  method: string,
  url: string,
  duration: number,
  status: number,
  requestHeaders: string,
  responseHeaders: string,
  absStartTime: number,
  requestBodySize: number,
  responseBodySize: number,
  requestBody: string | null,
  responseBody: string | null,
  requestLegibility?: AjaxLegibility,
  responseLegibility?: AjaxLegibility,
  state?: HttpRequestState,
];

export type Selection =
  | [] // No selection.
  | [
      anchorNode: FsId, // Node at which the caret is anchored. (Nothing is selected.)
      anchorOffset: number, // Anchor offset within the node.
    ]
  | [
      anchorNode: FsId, // Node at which the caret is anchored; start of the selection.
      anchorOffset: number, // Anchor offset within the node.
      focusedNode: FsId, // Node which is focused; end of the selection.
      focusOffset: number, // Focus offset within the node.
      anchorIsBeforeFocus: boolean, // True if the anchor is before the focus.
    ];

// The types of animations that can be recorded
export namespace RecAnimationType {
  export const Animation = 0; // Animations created via Web API
  export const CSSAnimation = 1;
  export const CSSTransition = 2;
}
export type RecAnimationType = (typeof RecAnimationType)[keyof typeof RecAnimationType];

// Recorded Animation methods and arguments
type RecAnimationMethodCall =
  | [name: 'cancel', args: []]
  | [name: 'commitStyles', args: []]
  | [name: 'finish', args: []]
  | [name: 'pause', args: []]
  | [name: 'persist', args: []]
  | [name: 'play', args: []]
  | [name: 'reverse', args: []]
  | [name: 'updatePlaybackRate', args: [playbackRate: number]];

// Recorded Animation properties and values
type RecAnimationPropertyChange =
  | [name: 'currentTime', value: number | null]
  | [name: 'effect', value: number | null]
  | [name: 'playbackRate', value: number]
  | [name: 'startTime', value: number | null]
  | [name: 'timeline', value: number | null];

// Primitive KeyframeEffect types
type CompositeOperation = 'accumulate' | 'add' | 'replace';
type CompositeOperationOrAuto = CompositeOperation | 'auto';
type IterationCompositeOperation = 'accumulate' | 'replace';
type FillMode = 'auto' | 'backwards' | 'both' | 'forwards' | 'none';
type PlaybackDirection = 'alternate' | 'alternate-reverse' | 'normal' | 'reverse';

// Keyframe objects must be encoded to reduce size
export type EncodedKeyframe = [
  properties: [name: string, value: string | number | null | undefined][],
  composite?: CompositeOperationOrAuto,
  easing?: string | undefined,
  offset?: number | null | undefined,
];

export type EncodedKeyframeEffectOptions = [
  delay?: number,
  direction?: PlaybackDirection,
  duration?: number | string,
  easing?: string,
  endDelay?: number,
  fill?: FillMode,
  iterationStart?: number,
  // NOTE: this type was originally a number,
  // and therefore must be maintained as string | number for backwards compatibility
  iterations?: number | string,
  composite?: CompositeOperation,
  iterationComposite?: IterationCompositeOperation,
];

export type EncodedOptionalEffectTiming = [
  delay?: number,
  direction?: PlaybackDirection,
  duration?: number | string,
  easing?: string,
  endDelay?: number,
  fill?: FillMode,
  iterationStart?: number,
  // NOTE: this type was originally a number,
  // and therefore must be maintained as string | number for backwards compatibility
  iterations?: number | string,
];

// Recorded KeyframeEffect methods and args
type RecKeyframeEffectMethodCall =
  | [name: 'setKeyframes', args: [keyframes: EncodedKeyframe[]]]
  | [name: 'updateTiming', args: [timing: EncodedOptionalEffectTiming]];

// Recorded KeyframeEffeect properties and values
type RecKeyframeEffectPropertyChange =
  | [name: 'composite', value: CompositeOperation]
  | [name: 'iterationComposite', value: IterationCompositeOperation]
  | [name: 'target', value: FsId | null];

type EncodedStreamItem = string | number | [number, number];
// A DOM mutation stream, as generated by NodeEncoder, compressed via
// LZJGWEncoder.
export type EncodedStream = EncodedStreamItem[];
export type DeferredEncodedStream = (EncodedStreamItem | (() => EncodedStream))[];

// A touch id for touch events; this identifies e.g. a specific finger.
export type TouchId = number;

export type TouchEventData = [touchId: TouchId, xPos: number, yPos: number];

export namespace TimingScope {
  export const Internal = 0;
  export const Public = 1;
}
export type TimingScope = (typeof TimingScope)[keyof typeof TimingScope];
export namespace TimingCategory {
  export const Unknown = 0;
  export const Serialization = 1;
}
export type TimingCategory = (typeof TimingCategory)[keyof typeof TimingCategory];
export namespace TimingLabel {
  export const Unknown = 0;
  export const DomSnapshot = 1; // special cased NodeEncoding, indicates the initial dom encoding
  export const NodeEncoding = 2;
  export const LzEncoding = 3;
  export const ApplyRules = 4; // Cost of evaluating privacy rules
  export const ProcessMut = 5; // Cost of processing mutations each tick (aggregate of MUT_* for this mutation record set)
}
export type TimingLabel = (typeof TimingLabel)[keyof typeof TimingLabel];
export type SubTiming = [
  label: TimingLabel, // Enum for used to identify the timing being recorded
  durationMs: number, // The number of milliseconds in elapsed time since the previous timing or subtiming
];
export type Timing = [
  scope: TimingScope, // The scoping of the data
  category: TimingCategory, // Enum used for categorizing all user timings into logical groups
  label: TimingLabel, // Enum used to identify the timing being recorded
  startTimeMs: TimeStampMs, // The start time of the timing
  durationMs: number, // The number of milliseconds in elapsed time since start to report
  ...subTimings: SubTiming[],
];

export namespace FrameStatus {
  export const Unknown = 0;
  export const Successful = 1;
  export const BlocklistedFrame = 2;
  export const PartiallyLoaded = 3;
  export const MissingWindowOrDocument = 4;
  export const MissingDocumentHead = 5;
  export const MissingBodyOrChildren = 6;
  export const AlreadyDefined = 7;
  export const NoNonScriptElement = 8;
  export const Exception = 9;
}

export type FrameStatus = (typeof FrameStatus)[keyof typeof FrameStatus];

export const ModalApiNames = ['print', 'alert', 'confirm'] as const;

export type NavigationType = 'back_forward' | 'navigate' | 'prerender' | 'reload';
export type LogSource = 'fs' | 'console';
export type LogLevel =
  | 'log'
  | 'info'
  | 'warn'
  | 'error'
  | 'debug'
  | '_fs_debug'
  | 'assert'
  | 'trace';

// Context information shared by all kinds of RawEvents.
export interface RawEventContext {
  // The sequence of ancestor frame ids leading from the outermost frame to the
  // frame containing this event. This is an optional field; we only set it if
  // we have 2 or more levels of iframe nesting.
  PIds?: FsId[];

  // Optional FrameId. Unset or 0 means outer/top window.
  FId?: FsId;

  // The event's timestamp. Relative to page start.
  When: TimeStampMs;
}

export type CaptureSourceType = 'default' | 'api' | 'server';

export type CanvasMetaInfo = {
  ctor?: string; // name of the constructor if it's an object
  id?: number; // if the arg is a node id, this is the fsid (if exists) of the node
  ref?: number; // if this an object we kept a weak reference of
  src?: string; // if this was an image, the src attribute of the image
  srcId?: number | { id: number }; // if this was another canvas, id of the source canvas
  typedArray?: string; // JSON serialized
  obj?: { [key: string]: unknown }; // if we couldn't distinguish the type, we store the key: values of this arg
  undef?: boolean; // true iff this argument was undefined, because undefined gets serialized to null
};
export type CanvasArgs = CanvasMetaInfo | number | string | bigint | boolean | null | CanvasArgs[];

export enum BfcacheState {
  Unset = 0,
  Entering,
  Restored,
}

// helper types to prevent recursion in the type system
type ResolvedRawEvent = Extract<
  RawEventName,
  'MUT_INSERT' | 'MUT_SHADOW' | 'CLICK' | 'EVENT_CANCELED' | 'VALUECHANGE'
>;
export type ResolvedEventArgs<EventName extends ResolvedRawEvent = ResolvedRawEvent> =
  EventName extends ResolvedRawEvent
    ? [(typeof RawEventKind)[EventName], () => RawEventArgs[EventName]]
    : never;

// The arguments associated with each kind of RawEvent. All RawEvents accept an
// array of arguments, but the details vary for each kind.
export type RawEventArgs = {
  MUT_INSERT: [
    parent: FsId, // Parent to insert the subtree under. (-1 if no parent.)
    before: FsId, // Sibling to insert the subtree before. (-1 if subtree is the last child.)
    subtree: EncodedStream, // The subtree to insert.
  ];

  MUT_REMOVE: FsId[]; // A list of nodes to remove.

  MUT_ATTR: [
    node: FsId, // Node whose attribute is being modified.
    attr: string, // The attribute name.
    value: string | null, // The new value, or null to remove the attribute.
  ];

  MUT_TEXT: [
    node: FsId, // The text node whose content has changed.
    text: string, // The text node's new content.
  ];

  MOUSEMOVE: [
    xPos: number,
    yPos: number,
    targetNode?: FsId, // The target of the 'mousemove' event. (Missing on old recordings.)
  ];

  MOUSEMOVE_CURVE: [
    endTimeMs: TimeStampMs, // End time. (Start time is implicit.)
    ...xPosCurve: Hermite, // X position curve.
    ...yPosCurve: Hermite, // Y position curve.
    targetNode?: FsId, // The target of the 'mousemove' event. (Missing on old recordings.)
  ];

  // Scroll position changes for the layout viewport. When running on a browser
  // which exposes the position of both the layout and visual viewports, we'll
  // also use RawEventKind.SCROLL_VISUAL_OFFSET to record the visual viewport
  // position.  On other browsers, we pretend that both viewports have the same
  // position, so only this event gets sent.
  SCROLL_LAYOUT: [
    node: FsId, // The node whose scroll position has changed.
    leftPos: number,
    topPos: number,
  ];

  SCROLL_LAYOUT_CURVE: [
    endTimeMs: TimeStampMs, // End time. (Start time is implicit.)
    node: FsId, // The node whose scroll position has changed.
    ...leftPosCurve: Hermite,
    ...topPosCurve: Hermite,
  ];

  MOUSEDOWN: [xPos: number, yPos: number];

  MOUSEUP: [xPos: number, yPos: number];

  CLICK: ClickArgs;

  FOCUS: [
    node: FsId, // The focused node.
    userFocus?: boolean, // Whether the focus is user-initiated (equates to Event.isTrusted)
  ];

  VALUECHANGE: [
    node: FsId, // The input-like element whose value has changed.
    value: 'true' | 'false' | string, // The new value. True or false for checkbox/radio buttons.
    userChange?: boolean, // Whether the change is user-initiated (equates to Event.isTrusted); added 2021.05.20.
    visible?: boolean, // Whether the change is user-visible (element had non-zero bounding box); added 2021.05.20.
    init?: boolean, // Whether the change represents the initial state of the input-like element, this is needed for playback fidelity.
  ];

  // A change in size of the layout viewport (i.e., the browser window or
  // containing <iframe>).
  RESIZE_LAYOUT: [
    clientWidth: number, // Client width. (The area inside the scrollbars.)
    clientHeight: number, // Client height. (The area inside the scrollbars.)
    mediaQueryWidth?: number, // If different, the media query width. (The area including scrollbars.)
    mediaQueryHeight?: number, // If different, the media query height. (The area including scrollbars.)
  ];

  DOMLOADED: [];

  LOAD: [];

  PLACEHOLDER_SIZE: [
    node: FsId, // The excluded or masked node whose size we're capturing.
    width: number, // The width of its bounding client rect.
    height: number, // The height of its bounding client rect.
  ];

  UNLOAD: [reason: UnloadReason]; // Why did the page unload?

  BLUR: [
    node: FsId, // The blurred node.
    userBlur?: boolean, // Whether the blur is user-initiated (equates to Event.isTrusted)
  ];

  // Set the base URL and doctype of a document. Despite the name, we send this
  // for both top-level documents and documents inside frames. This is always
  // the first event we generate for a given document.
  //
  // The second arguments, referrer and URL, are also parameters given
  // to rec/page. This is prefactoring for a change that will redact the
  // rec/page parameters more heavily.
  SET_FRAME_BASE: [
    baseUrl: string, // The base URL of the document.
    doctype?: string, // The doctype of the document. (Not set in old recordings.)
    docUrl?: string, // document.URL - (Not set before May 2020, or in iframes.)
    docReferrer?: string, // document.referrer - (Not set before May 2020, or in iframes.)
  ];

  TOUCHSTART: TouchEventData;

  TOUCHEND: TouchEventData;

  TOUCHCANCEL: TouchEventData;

  TOUCHMOVE: TouchEventData;

  TOUCHMOVE_CURVE: [
    endTimeMs: TimeStampMs, // End time. (Start time is implicit.)
    touchId: number, // Touch id. (This identifies e.g. a specific finger.)
    ...xPosCurve: Hermite,
    ...yPosCurve: Hermite,
  ];

  // A semantic navigation that does not trigger the loading of a new document.
  // These are generated via the History API (pushState() etc.).
  // Note: second arg has been used twice historically, first for doc title,
  //       second for navigation type. Be aware if trying to extend the args here.
  NAVIGATE: [url: string];

  PLAY: [node: FsId]; // The media element which started playing.

  PAUSE: [node: FsId]; // The media element which paused.

  // Visual viewport size. This is used for pinch zoom on mobile browsers (and
  // desktop browsers that support it). We record the visual viewport size
  // rather than calculating the zoom factor on the client because we want to
  // calculate a spline for this value. Being non-linear (1/x), zoom factor
  // can't be interpolated this way. Instead, we just calculate the zoom factor
  // from this value during playback.
  RESIZE_VISUAL: [width: number, height: number];

  RESIZE_VISUAL_CURVE: [
    endTimeMs: TimeStampMs, // End time. (Start time is implicit.)
    ...widthCurve: Hermite,
    ...heightCurve: Hermite,
  ];

  // An educated guess of the size of the document.
  RESIZE_DOCUMENT_CONTENT: [width: number, height: number];

  // An educated guess of the internal scrollable size of an element.
  RESIZE_SCROLLABLE_ELEMENT_CONTENT: [fsId: number, width: number, height: number];

  // Arguments to console.*(), FS.log, or FS('log').
  // The first element is the source (LogSource) followed by log level.
  // NOTE: legacy LOG events omit source. Union type is for backward compatibility. -ScottN 12/14/2021
  LOG:
    | [source: LogSource, logLevel: LogLevel, ...args: string[]]
    | [logLevel: LogLevel, ...args: string[]];

  ERROR: [
    message: string, // Error message from the error event handler.
    scriptUrl: string, // Script URL from the error event handler.
    line: number, // Script line from the error event handler.
    ...stackFrames: StackFrame[], // Stack trace. (Not available on IE 9.)
  ];

  DBL_CLICK: [node: FsId];

  FORM_SUBMIT: [formNode: FsId]; // The form element that was submitted.

  WINDOW_FOCUS: [];

  WINDOW_BLUR: [];

  HEARTBEAT: [timeSinceLastUserActivity: number];

  WATCHED_ELEM: [
    node: FsId, // The watched element.
    isNowVisible: boolean, // True if the element became visible; false if it was hidden.
  ];

  /**
   * The parts of IntersectionObserverEntry we care about. These events
   * are only sent for watched elements.
   */
  VIEWPORT_INTERSECTION: [targetNode: FsId, intersectionRatio: number];
  COPY: [];
  PASTE: [];

  // Performance entries. The first element is the performance entry type.
  PERF_ENTRY: [PerfEntryType, ...(number | string)[]];

  LONG_FRAME: [
    startMs: TimeStampMs, // the starting When time
    frameDurations: TimeStampMs[], // consecutive frame times
  ];

  // Records information about features that are supported and enabled for this
  // recording. This is an event, rather than a /rec/page argument, because we
  // don't know until after recording starts.
  REC_FEAT_SUPPORTED: [
    RecFeature,
    boolean, // Repeated [feature type, supported] tuple.
    RecFeature?,
    boolean?,
    RecFeature?,
    boolean?,
    RecFeature?,
    boolean?,
    RecFeature?,
    boolean?,
    RecFeature?,
    boolean?,
    RecFeature?,
    boolean?,
    RecFeature?,
    boolean?,
    RecFeature?,
    boolean?,
  ];

  SELECT: Selection;

  CSSRULE_INSERT: [
    sheet: FsId | SheetQualifiedIdentity, // The stylesheet which is changing.
    rules: string | string[], // The rules to insert. (2019-10: The 'string' version is deprecated.)
    index?: number, // The index at which to insert the new rules. (Omitted if 0.)
    path?: number[], // list of indices leading to the parent of this rule (see https://drafts.csswg.org/cssom/#the-cssgroupingrule-interface)
  ];

  CSSRULE_DELETE: [
    sheet: FsId | SheetQualifiedIdentity, // The stylesheet which is changing.
    index: number, // The index of the rule to delete.
    path?: number[], // list of indices leading to the parent of this rule (see https://drafts.csswg.org/cssom/#the-cssgroupingrule-interface)
  ];

  // Sent when hooks that affect performance are throttled to avoid slowing down
  // the customer's page during recording. This generally breaks the recording,
  // since we'll miss some events.
  FAIL_THROTTLED: [task: ThrottledTask]; // What was throttled?

  AJAX_REQUEST: AjaxRequestArgs;

  // Scrolling position changes for the visual viewport, specified as an offset
  // from the layout viewport. This information is only available for window
  // scrolling (as opposed to scrollable elements within the document), and
  // we're currently only able to distinguish between layout and visual viewport
  // positions on some browsers.
  SCROLL_VISUAL_OFFSET: [
    node: FsId, // The scrolling element.
    leftOffset: number,
    topOffset: number,
  ];

  SCROLL_VISUAL_OFFSET_CURVE: [
    endTimeMs: TimeStampMs, // End time. (Start time is implicit.)
    node: FsId, // The scrolling element.
    ...leftOffsetCurve: Hermite,
    ...topOffsetCurve: Hermite,
  ];

  // Media query feature values that match. Note that some features may change
  // over the course of a session!
  MEDIA_QUERY_CHANGE: [
    feature: string, // Feature name.
    value: string, // The value for that feature that matches.
  ];

  RESOURCE_TIMING_BUFFER_FULL: [];

  MUT_SHADOW: [
    shadowHost: FsId, // The node to which the shadow root is attached.
    subtree: EncodedStream, // The shadow subtree attached to that node.
  ];

  DISABLE_STYLESHEET: [
    sheet: FsId | SheetQualifiedIdentity, // The stylesheet which is changing.
    disabled: boolean, // True if the stylesheet is now disabled; false if it is now enabled.
  ];

  FULLSCREEN: [
    node: FsId, // The fullscreen element.
    fullscreen: boolean, // True if the element is now fullscreen; false if it's not.
  ];

  FULLSCREEN_ERROR: [];

  ADOPTED_STYLESHEETS: [
    documentOrShadowRoot: FsId, // The document or shadow root which is adopting the stylesheets.
    sheets: number[], // The sheet ids of the adopted stylesheets.
  ];

  CUSTOM_ELEMENT_DEFINED: [
    tagName: string, // element tag being defined
  ];

  // MODAL_OPEN and MODAL_CLOSE bookend a call to an API that opens
  // a native mobile. Since these APIs block the thread while the modal
  // is open, the event before any MODAL_CLOSE should be a MODAL_OPEN
  // with the same first argument.
  MODAL_OPEN: [
    api: ElementOf<typeof ModalApiNames>, // Native modal API being called.
  ];
  MODAL_CLOSE: [
    api: ElementOf<typeof ModalApiNames>, // Native modal API being called.
  ];

  TIMING: Timing[]; // Performance timing info

  // STORAGE_WRITE_FAILURE reports a failure to write the fs_uid cookie,
  // indicating that localstorage or cookies are disabled. Currently
  // used only for debugging.
  STORAGE_WRITE_FAILURE: [
    value: string, // Thing being stored
    storageKind: StorageKind,
    message?: string, // Error message; omitted if the write failed silently.
  ];

  DOCUMENT_PROPERTIES: [
    scrollingElement: FsId, // document's scolling element
    compatMode: string, // document's compatMode
  ];

  // A semantic navigation that is trigged on the loading of a new document.
  // Note: tThis event is used for backwards compatibility, the cook creates
  //       a synthetic NAVIGATE event for legacy pages, newer pages we now
  //       guarantee this event will be present.
  ENTRY_NAVIGATE: [url: string, type: NavigationType | 'unknown'];

  ANIMATION_CREATED: [
    id: number, // animation ID
    type: RecAnimationType,
    effectId: number | undefined,
    timelineId: number | undefined,
  ];

  ANIMATION_METHOD_CALLED: [
    id: number, // animation ID
    data: RecAnimationMethodCall,
  ];

  ANIMATION_PROPERTY_SET: [
    id: number, // animation ID
    data: RecAnimationPropertyChange,
  ];

  DOCUMENT_TIMELINE_CREATED: [
    id: number, // document timeline ID
    originTime: number | undefined,
  ];

  KEYFRAME_EFFECT_CREATED: [
    id: number, // KeyframeEffect ID,
    targetId: FsId,
    keyframes: EncodedKeyframe[],
    options?: EncodedKeyframeEffectOptions,
  ];

  KEYFRAME_EFFECT_METHOD_CALLED: [
    id: number, // KeyframeEffect ID,
    data: RecKeyframeEffectMethodCall,
  ];

  KEYFRAME_EFFECT_PROPERTY_SET: [
    id: number, // KeyframeEffect ID,
    data: RecKeyframeEffectPropertyChange,
  ];

  INIT_API: [
    'privacy',
    [
      'attributeBlocklist',
      [
        AttributeBlockEntryForApi['target'],
        AttributeBlockEntryForApi['tag'],
        AttributeBlockEntryForApi['name'],
        AttributeBlockEntryForApi['action'],
        AttributeBlockEntryForApi['type'],
      ][],
    ],
    'env',
    [key: string, value: string][],
  ];

  KEEP_ELEMENT: [
    element: FsId, // The matching element
  ];

  KEEP_URL: [
    url: string, // The matching url
  ];

  KEEP_BOUNCE: [
    boundMs: number, // The BounceMillis setting from the org's FS.Keep config
  ];

  KEEP_CRASH: [];

  STATS: [
    eventType: string, // Name of the Stat Event
    payload: string, // Statistics Payload (in truncated JSON)
  ];

  SYS_SETVAR: [
    scope: VarScope, // Variable scope.
    payload: string, // Variable JSON payload.
    apiSource?: string, // API source, if different from the default.
    apiVersion?: number,
    schema?: string, // Variable JSON schema
  ];

  // Report a canonical (fsnv) resource hash for url rewriting.
  SYS_RESOURCEHASH: [
    kind: 'url', // The type of resource being hashed. Might support "textContent" or "attribute" in the future.
    resource: string, // The resource being hashed. (e.g. "blob:http://example.com/3556cbf2-9652")
    hash: string, // The fsnv hash of the resource content. (e.g. "2ihPDhRAaQS/W6WbIh/lFw==")
  ];

  // Set a new value for the user's consent state. (This is normally in response
  // to a call to the FS('setIdentity', { consent }) API.) See also RawEventKind.SYS_REPORTCONSENT.
  SYS_SETCONSENT: [
    consentState: ConsentState, // True if the user consented.
    scope?: ConsentScope,
  ];

  // For "custom events" sent to FS('trackEvent') from the client.
  SYS_CUSTOM: [
    event: string, // Event name.
    payload: string, // Event JSON payload.
    apiSource?: string, // API source, if different from the default.
    apiVersion?: number,
    schema?: string, // Event JSON schema.
  ];

  // Report the existing value for the user's consent state. This is purely
  // informational; the server takes no action in response to this event. See
  // also RawEventKind.SYS_SETCONSENT.
  SYS_REPORTCONSENT: [
    consentState: ConsentState, // True if the user consented.
    scope?: ConsentScope,
  ];

  // This is a pseudo event which is used by mobile lethe and should
  // never show up in an event stream post-lethe.
  SYS_LETHE_MOBILE_BUNDLE_SEQ: [seq: number];

  URL_SALT: [salt: string];
  URL_ID: [url: string, id: number][];

  FRAME_STATUS: [frameId: FsId, frameDesc: string, frameStatus: FrameStatus];

  // Same as the CompiledTimestamp property on Page objects in Datastore
  SCRIPT_COMPILED_VERSION: [version: string];

  RESET_CSS_SHEET: [sheet: FsId | SheetQualifiedIdentity];

  CAPTURE_SOURCE: [
    type: CaptureSourceType,
    Entrypoint: string | undefined,
    Origin: string | undefined,
    Integration: string | undefined,
    UserInitiated: boolean,
  ];

  PAGE_DATA: [
    url: string,
    base: string,
    width: number,
    height: number,
    screenWidth: number,
    screenHeight: number,
    snippetVersion: string,
    referrer: string,
    doctype: string,
    preroll: number,
    prevFsUid: string,
    pageStart: number,
    /**
     * @deprecated
     * For stateless capture Accept-Language is now retrieved from request headers instead of PAGE_DATA so stop populating
     * this at some point.
     */
    acceptLanguage: string,
    /**
     * @deprecated
     * For stateless capture User-Agent is now retrieved from request headers instead of PAGE_DATA so stop populating
     * this at some point.
     */
    userAgent: string,
    /**
     * Identifier for the browser tab. Can be undefined meaning "unknown"
     * for old recordings, or if we couldn't read SessionStorage.
     */
    tabId: string | undefined,
    isEntry: boolean,
    previewMode: boolean,
    appId: string | undefined,
    appkeyHash: string | undefined,
    compiledTimestamp: number,
    compiledVersion: string,
    sessionKeyHash: string,
    pageKeyHash: string,
  ];

  VISIBILITY_STATE: [visibilityState: string];

  DIALOG: [node: FsId, open: boolean, modal?: boolean];

  CSSRULE_UPDATE: [
    id: CssRuleId, // Either the internal ID, or an array with the Sheet ID and index of the rule
    rules: { [key: string]: string }, // Map CSS properties to updated values
  ];

  CANVAS: [
    target: FsId | DetachedCanvasId,
    ops: [
      context: string,
      type: CanvasOpType,
      method: string,
      args: CanvasArgs[],
      returnId: number,
    ][],
  ];

  CANVAS_DETACHED_DIMENSION: [id: DetachedCanvasId, width: number, height: number];

  DEFERRED_RESOLVED: FsId[]; // A list of deferred nodes whose children have now been encoded.

  ELEMENT_PROP: [
    element: FsId, // element id
    key: string, // property name
    value: unknown, // property value
  ];

  BFCACHE_STATE: [state: BfcacheState];

  SESSION_INFO: [
    reason: string, // details about why the previous session was closed
  ];

  EVENT_CANCELED: [
    type: EventCanceledType, // type of event that was canceled
    data: ClickArgs, // synthetic click event data
  ];

  DIAGNOSTIC_INFO: [
    msg: string, // diagnostic information
  ];

  RESOLVABLE_EVENT: ResolvedEventArgs;

  IN_SESSION_PREDICTION: [
    modelType: string, // name and model version
    predictedClass: string, // class label
    flowCompletionProbability: number, // probability for class WILL_COMPLETE
  ];
};

export enum CssRuleIdKind {
  Index = 1,
  Cached = 2,
}

export type CssRuleId =
  | [
      type: CssRuleIdKind.Index,
      sheet: FsId | SheetQualifiedIdentity,
      index: number,
      path?: number[],
    ]
  | [type: CssRuleIdKind.Cached, id: number];

// A discriminated union representing the data associated with a RawEvent.
// NOTE: If the compiler complains that 'EventName cannot be used to index
// typeof RawEventKind' here, that means that you added a new event to
// RawEventKind but forgot to define its arguments in RawEventArgs.
export type RawEventData<EventName extends RawEventName = RawEventName> =
  EventName extends RawEventName
    ? {
        Kind: (typeof RawEventKind)[EventName];
        Args: RawEventArgs[EventName];
      }
    : never;

export type RawEvent = RawEventContext & RawEventData;

// A RawEvent of a specific kind - for example, RawEvent<'CLICK'>.
export type RawEventOfKind<EventName extends RawEventName> = RawEventContext &
  SelectFromUnion<
    RawEventData,
    {
      Kind: (typeof RawEventKind)[EventName];
    }
  >;

export namespace ConsentState {
  export const GrantConsent = true;
  export const RevokeConsent = false;
}
export type ConsentState = (typeof ConsentState)[keyof typeof ConsentState];

export namespace ConsentScope {
  export const Page = 0;
  export const Document = 1;
}
export type ConsentScope = (typeof ConsentScope)[keyof typeof ConsentScope];

// Constants used in RawEventKind.UNLOAD.
// todo(clint) comments display legacy reason values, remove after Nov 2019
export namespace UnloadReason {
  // reserve 0 for "unknown" for Golang interop
  export const Unknown = 0;
  // "FS.shutdown()" | "_fs_shutdown"
  export const Api = 1;
  // "ShutdownFrame message" | "detached iframe" | "stale iframe"
  export const FsShutdownFrame = 2;
  // "hibernation" | "post-hibernation"
  export const Hibernation = 3;
  // "reidentify"
  export const Reidentify = 4;
  // "unsupported browser" | "invalid config" | "because /rec/page said so"
  export const SettingsBlocked = 5;
  // "size"
  export const Size = 6;
  // "pagehide" | "unload"
  export const Unload = 7;
  // document.visibilityState is "hidden"
  export const Hidden = 8;
}
export type UnloadReason = (typeof UnloadReason)[keyof typeof UnloadReason];

/**
 * Reasons why the Ajax req/res body was empty
 */
export namespace AjaxLegibility {
  export const Unknown = 0;
  export const NotEmpty = 1;
  // content-length == 0
  export const EmptyBody = 2;
  // TODO(erik): Add the rest of the cases
}
export type AjaxLegibility = (typeof AjaxLegibility)[keyof typeof AjaxLegibility];

/** rough approximations to response.type https://developer.mozilla.org/en-US/docs/Web/API/Response/type */
export enum HttpRequestState {
  UNSET = 0,
  OK,
  ABORTED,
  OPAQUE,
  ERROR,
}

/**
 * Values from the browser Performance API, for use with
 * RawEventKind.PERF_ENTRY.
 */
export namespace PerfEntryType {
  export const Timing = 0;
  export const Navigation = 1;
  export const Resource = 2;
  export const Paint = 3;
  export const Mark = 4;
  export const Measure = 5;
  export const Memory = 6;
  export const TimeOrigin = 7;
  export const LayoutShift = 8;
  export const FirstInput = 9;
  export const LargestContentfulPaint = 10;
  export const LongTask = 11;
  export const EventTiming = 12;
  export const EventTimingCount = 13;
  export const LongAnimationFrame = 14;
  export const ScriptTiming = 15;
}
export type PerfEntryType = (typeof PerfEntryType)[keyof typeof PerfEntryType];

/**
 * Field orders for the various performance entry types.
 * NOTE: Do _not_ change the ordering. They are the sole definition of what's in
 * the args field.
 * TODO(sethf): Change this to a mapped type where the keys are PerfEntryType.
 * We currently can't do this because PerfEntryType is a union of numeric
 * literal types, and we're compiling with keyofStringsOnly because of some
 * difficulties with index signature types elsewhere in the code base.
 * Fortunately, those problems should go away with TypeScript 4.2, thanks to
 * this PR:
 *   https://github.com/microsoft/TypeScript/pull/26797
 */
export namespace PerfEntryFields {
  /**
   * PerfEntryType.Timing
   * Note: these are absolute times from performance.timing, largely overlapping
   * with 'navigation' below, but they're available on more browsers.
   */
  export const Timing: readonly string[] = [
    'navigationStart', // 01
    'unloadEventStart', // 02
    'unloadEventEnd', // 03
    'redirectStart', // 04
    'redirectEnd', // 05
    'fetchStart', // 06
    'domainLookupStart', // 07
    'domainLookupEnd', // 08
    'connectStart', // 09
    'connectEnd', // 10
    'secureConnectionStart', // 11
    'requestStart', // 12
    'responseStart', // 13
    'responseEnd', // 14
    'domLoading', // 15
    'domInteractive', // 16
    'domContentLoadedEventStart', // 17
    'domContentLoadedEventEnd', // 18
    'domComplete', // 19
    'loadEventStart', // 20
    'loadEventEnd', // 21
  ];

  /** PerfEntryType.Navigation */
  export const Navigation: readonly string[] = [
    'name', // 01
    'startTime', // 02
    'duration', // 03

    'initiatorType', // 04
    'redirectStart', // 05
    'redirectEnd', // 06
    'fetchStart', // 07
    'domainLookupStart', // 08
    'domainLookupEnd', // 09
    'connectStart', // 10
    'connectEnd', // 11
    'secureConnectionStart', // 12
    'requestStart', // 13
    'responseStart', // 14
    'responseEnd', // 15

    'unloadEventStart', // 16
    'unloadEventEnd', // 17
    'domInteractive', // 18
    'domContentLoadedEventStart', // 19
    'domContentLoadedEventEnd', // 20
    'domComplete', // 21
    'loadEventStart', // 22
    'loadEventEnd', // 23
    'type', // 24
    'redirectCount', // 25

    // Possibly non-standard, but supported by chrome+firefox (not edge).
    'decodedBodySize', // 26
    'encodedBodySize', // 27
    'transferSize', // 28
    'activationStart', // 29
  ];

  /** PerfEntryType.Resource */
  export const Resource: readonly string[] = [
    'name', // 01
    'startTime', // 02
    'duration', // 03

    'initiatorType', // 04
    'redirectStart', // 05
    'redirectEnd', // 06
    'fetchStart', // 07
    'domainLookupStart', // 08
    'domainLookupEnd', // 09
    'connectStart', // 10
    'connectEnd', // 11
    'secureConnectionStart', // 12
    'requestStart', // 13
    'responseStart', // 14
    'responseEnd', // 15

    // Possibly non-standard, but supported by chrome+firefox (not edge).
    'decodedBodySize', // 16
    'encodedBodySize', // 17
    'transferSize', // 18
  ];

  /** PerfEntryType.[Paint Mark Measure] */
  export const Measure: readonly string[] = [
    'name', // 01
    'startTime', // 02
    'duration', // 03
  ];

  /** PerfEntryType.LongAnimationFrame */
  export const LongAnimationFrame: readonly string[] = [
    'name', // 01
    'startTime', // 02
    'duration', // 03

    'renderStart', // 04
    'styleAndLayoutStart', // 05
    'blockingDuration', // 06
    'firstUIEventTimestamp', // 07
  ];

  export const ScriptTiming: readonly string[] = [
    'name', // 01
    'startTime', // 02
    'duration', // 03

    'invokerType', // 04
    'invoker', // 05
    'executionStart', // 06
    'sourceURL', // 07
    'sourceFunctionName', // 08
    'sourceCharPosition', // 09
    'pauseDuration', // 10
    'forcedStyleAndLayoutDuration', // 11
    'windowAttribution', // 12
  ];

  /** PerfEntryType.Memory */
  export const Memory: readonly string[] = ['jsHeapSizeLimit', 'totalJSHeapSize', 'usedJSHeapSize'];

  /** PerfEntryType.TimeOrigin */
  export const TimeOrigin: readonly string[] = ['timeOrigin'];

  export const LayoutShift: readonly string[] = ['startTime', 'value', 'hadRecentInput'];

  /**
   * @deprecated
   * FirstInput is generically a PerformanceEventTiming and should be collapsed into that type
   */
  export const FirstInput: readonly string[] = ['name', 'startTime', 'duration', 'processingStart'];

  export const EventTiming: readonly string[] = [
    'name', // event name (one of https://developer.mozilla.org/en-US/docs/Web/API/PerformanceEventTiming#events_exposed)
    'startTime',
    'duration',
    'processingStart',
    'processingEnd',
    'interactionId',
    'target', // target node
  ];

  export const LargestContentfulPaint: readonly string[] = [
    'name',
    'startTime',
    'duration',
    'renderTime',
    'loadTime',
    'size',
    'element', // target node
  ];

  // performance.interactionCount
  export const EventTimingCount: readonly string[] = ['interactionCount'];
}
export type PerfEntryFields = (typeof PerfEntryFields)[keyof typeof PerfEntryFields];

// Feature types for RawEventKind.REC_FEAT_SUPPORTED:
export namespace RecFeature {
  // performance[.timing] (not supported on all browsers)
  export const Performance = 0;

  // performance.entries (same; without it we don't get navigation, paint, or
  // mark/measure data)
  export const PerformanceEntries = 1;

  // performance.memory (same)
  export const PerformanceMemory = 2;

  // console recording enabled (optional)
  export const Console = 3;

  // ajax recording enabled (optional)
  export const Ajax = 4;

  // PerformanceObserver (required to record performance entries beyond the
  // resource timing buffer)
  export const PerformanceObserver = 5;

  // Reserved: AjaxFetch = 6

  // performance.timeOrigin
  export const PerformanceTimeOrigin = 7;

  // web animation recording supported
  export const WebAnimation = 8;

  export const LayoutShift = 9;

  export const FirstInput = 10;

  export const LargestContentfulPaint = 11;

  export const LongTask = 12;

  export const HTMLDialogElement = 13;

  export const CaptureOnStartupEnabled = 14;

  export const CanvasWatcherEnabled = 15;

  export const CanvasScreenShotMode = 16;

  export const ResizeObserver = 17;
}
export type RecFeature = (typeof RecFeature)[keyof typeof RecFeature];

export namespace SheetOwner {
  export const Node = 1; // A <style> or <link> element.
  export const Sheet = 2; // A constructed style sheet.
}
export type SheetOwner = (typeof SheetOwner)[keyof typeof SheetOwner];
export type SheetQualifiedIdentity = Readonly<[SheetOwner, number]>;
export type DetachedCanvasId = { id: number };

export type EventCanceledType = 'pointerdown' | 'pointerup' | 'touchend';
export type ClickTarget = [
  targetNode: FsId, // The target of the click event.
  clientX: number, // The X position of the click event in local coordinates. ('clientX')
  clientY: number, // The Y position of the click event in local coordinates. ('clientY')
];
export type ClientRectArgs = [left: number, top: number, width: number, height: number];
export type ClickArgs = [
  ...ClickTarget,
  ...ClientRectArgs, // The bounding client rect of the target node.
];

// Throttled task types for RawEventKind.FAIL_THROTTLED.
export namespace ThrottledTask {
  export const StyleSheetHooks = 0;
  export const SetPropertyHooks = 1;
}
export type ThrottledTask = (typeof ThrottledTask)[keyof typeof ThrottledTask];

// Scopes for RawEventKind.SYS_SETVAR.
export namespace VarScope {
  export const Document = 'document';
  export const Event = 'evt';
  export const Page = 'page';
  export const User = 'user';
}
export type VarScope = (typeof VarScope)[keyof typeof VarScope];

export enum HighlightType {
  FsId = 'fsidentity',
  NewUid = 'newuid',
}

export interface ApiResult {
  events: RawEvent[];
  /** signals the user has re-identified */
  reidentify: boolean;
  /** appId of the current user */
  appId?: string;
}

// NOTE: Keep isUserActionEvent and isUserVisibleEvent in sync with
// their counterparts in the fs/services/recorder/clientapi Go package.
export function isUserActionEvent(kind: RawEventKind): boolean {
  switch (kind) {
    case RawEventKind.MOUSEDOWN:
    case RawEventKind.MOUSEMOVE:
    case RawEventKind.MOUSEMOVE_CURVE:
    case RawEventKind.MOUSEUP:
    case RawEventKind.TOUCHSTART:
    case RawEventKind.TOUCHEND:
    case RawEventKind.TOUCHMOVE:
    case RawEventKind.TOUCHMOVE_CURVE:
    case RawEventKind.TOUCHCANCEL:
    case RawEventKind.CLICK:
    case RawEventKind.SCROLL_LAYOUT:
    case RawEventKind.SCROLL_LAYOUT_CURVE:
    case RawEventKind.SCROLL_VISUAL_OFFSET:
    case RawEventKind.SCROLL_VISUAL_OFFSET_CURVE:
    case RawEventKind.NAVIGATE:
      return true;
  }
  return false;
}

// Events apparent to the end-user that may have been an intended
// result of an interaction (i.e. not a dead click)
export function isUserVisibleEvent(kind: RawEventKind): boolean {
  switch (kind) {
    case RawEventKind.VALUECHANGE:
    case RawEventKind.SCROLL_LAYOUT:
    case RawEventKind.SCROLL_LAYOUT_CURVE:
    case RawEventKind.SCROLL_VISUAL_OFFSET:
    case RawEventKind.SCROLL_VISUAL_OFFSET_CURVE:
    case RawEventKind.MUT_INSERT:
    case RawEventKind.MUT_REMOVE:
    case RawEventKind.MUT_ATTR:
    case RawEventKind.MUT_SHADOW:
    case RawEventKind.MUT_TEXT:
    case RawEventKind.NAVIGATE:
    case RawEventKind.LOAD:
    case RawEventKind.FOCUS:
    case RawEventKind.BLUR:
    case RawEventKind.SELECT:
    case RawEventKind.FORM_SUBMIT:
    case RawEventKind.PLAY:
    case RawEventKind.PAUSE:
    case RawEventKind.MODAL_OPEN:
    case RawEventKind.MODAL_CLOSE:
      return true;
  }
  return false;
}

export type StackFrame = [string, string, number, number]; // func, file, line, col

export interface RecPageParams {
  OrgId: string;
  UserId: string;
  SessionId: string; // added for stateless capture
  PageId: string; // added for stateless capture
  Url: string;
  Base: string;
  Width: number;
  Height: number;
  ScreenWidth: number;
  ScreenHeight: number;
  Referrer: string;
  Preroll: number; // msec before FS When=0
  Doctype: string;
  CompiledVersion: string; // GIT_SHORT_HASH fs.js script file was compiled at.
  CompiledTimestamp: number;
  AppId?: string;
  ReplayFlags?: string;
  Fallback?: boolean;
  FreshOrgForTest?: boolean; // For use in tests only.
  PreviewMode?: boolean; // whether to request preview block rules from /rec/page
  SnippetVersion?: string; // FS._v on the snippet
  TabId?: string; // fs.js-generated identifier for the page's browser tab.
  SessionKeyHash?: string; // A client generated "password" that allows the containing session to be shared; ignored if not the first page of session.
  PageKeyHash?: string; // A client generated "password" that allows this page to be shared.
}

// Constants used in AjaxWatch.[ReqRsp]BodyType.
export namespace AjaxBodyPolicy {
  // Elide the body value.
  export const Elide = 0;
  // Record the body in its entirety.
  export const Record = 1;
  // Record the body subject to allowlist, only if in a parseable format.
  export const Allowlist = 2;
}
export type AjaxBodyPolicy = (typeof AjaxBodyPolicy)[keyof typeof AjaxBodyPolicy];

export interface AjaxWatch {
  URLRegex: string; // Regex matching Ajax URLs.
  RecordReq: AjaxBodyPolicy; // Whether to record request bodies.
  RecordRsp: AjaxBodyPolicy; // Ditto, for responses.
  ReqAllowlist: string; // Request allowlist; only used for AjaxBodyPolicy.Allowlist.
  RspAllowlist: string; // Ditto, for responses.
}

export interface MobileInitData {
  StartTime: number;
  Host: string;
  OrgId: string;
  PageResponse: PageResponse; // JSON blob provided to mobile recording integration, passed as-is to client
}

export type Pattern = {
  /** regular expression string */
  Expression: string;
};

export type PatternSet = {
  Hash?: Pattern[];
  Host?: Pattern[];
  Path?: Pattern[];
  /** Whole search string */
  Query?: Pattern[];
  /** Individual key=value params */
  QueryParam?: Pattern[];
};

export type UrlPrivacyRule = {
  /** The condition for this rule; if not present, the rule is unconditional. */
  If?: PatternSet[];
  /** Exclusions to apply if the condition is true. This too is just a PatternSet. */
  Exclude: PatternSet;
};

// enums used for attribute blocking; these should match the values found in recording.proto
export namespace AttributeBlockTarget {
  export const Any = 0;
  export const Exclude = 1;
  export const Mask = 2;
}
export type AttributeBlockTarget = (typeof AttributeBlockTarget)[keyof typeof AttributeBlockTarget];

/**
 * List of possible actions to be performed on a blocked attribute
 * NOTE: this must be in descending privacy order; that is, the more strict rules must come first
 * When there are overlapping entries, the lowest value action is used
 */
export namespace AttributeBlockAction {
  export const Erase = 0;
  export const MaskText = 1;
  export const ScrubUrl = 2;
  export const ScrubCss = 3;
}
export type AttributeBlockAction = (typeof AttributeBlockAction)[keyof typeof AttributeBlockAction];

export namespace AttributeBlockType {
  export const Static = 0;
  export const Prefix = 1;
}
export type AttributeBlockType = (typeof AttributeBlockType)[keyof typeof AttributeBlockType];

/** entry for attribute blocklist */
export interface AttributeBlockEntry {
  /** what state of the node does this rule target; valid values are Any, Exclude, Mask */
  Target: AttributeBlockTarget;
  /** what tag does this rule target */
  Tag: string;
  /** what attribute does this rule target, required for static attribute blocks */
  Name?: string;
  /** what to do with the attribute, valid values are maskText, erase, scrubUrl, scrubCss */
  Action: AttributeBlockAction;
  /** optional type of attribute, omitted by if it's a static attribute, valid value is "prefix" */
  Type?: AttributeBlockType;
}

export interface OrgSettings {
  // Limit number of performance.mark|measure entries we record
  MaxPerfMarksPerPage: number;
  // Limit number of logs we record
  MaxConsoleLogPerPage: number;
  // Limit the size of the ajax request and response bodies we record
  MaxAjaxPayloadLength: number;
  // Limit the length of URLs we ingest
  MaxUrlLength: number;
  // Toggle for recording performance resource image timings.
  RecordPerformanceResourceImg: boolean;
  // Toggle for recording performance resource timings.
  // https://developer.mozilla.org/en-US/docs/Web/API/PerformanceResourceTiming
  RecordPerformanceResourceTiming: boolean;
  // HTTP request headers to allow
  HttpRequestHeadersAllowlist: string[];
  // HTTP response headers to allow
  HttpResponseHeadersAllowlist: string[];
  // Conditional URL privacy rules
  UrlPrivacyConfig: UrlPrivacyRule[];
  /** list of attributes to block */
  AttributeBlocklist?: AttributeBlockEntry[];
}

// Static data fetched from edge servers
export interface SettingsResponse {
  AjaxWatches: AjaxWatch[];
  BehaviorSignalSettings?: BehaviorSignalSettings;
  CookieDomain: string;
  DwellTime: number;
  ElementBlocks: ElementBlock[];
  ElementDeferreds: ElementDefer[];
  ElementKeeps: ElementKeep[];
  ElementWatches: ElementWatch[];
  GCLBSubdomain?: string;
  OrgSettings: OrgSettings;
  UrlKeeps: UrlKeep[];

  /**
   * If provided, overrides DefaultBundleUploadInterval. Reftests use this to
   * speed up the recording process; we don't use this in non-test contexts.
   */
  BundleUploadInterval?: number;

  /**
   * If provided, overrides HeartbeatInterval. Reftests use this to
   * speed up the recording process; we don't use this in non-test contexts.
   */
  HeartbeatInterval?: number;

  // reserved: Domains
}

export interface RecPageResponse {
  // Optional so we can safely drop in the future.
  // Set when stateless capture is enabled, contains the 128bit version of a 64bit UserId
  UserUUID?: string;
  SessionUUID?: string;
  PageUUID?: string;
  UserIntId: string;
  SessionIntId: string;
  PageIntId: string;
  EmbedToken: string; // Optionally set to an encrypted token granting access to an embed link
  PageStart: number;
  IsNewSession: boolean;
  PreviewMode?: boolean;
  Flags: PageFlags;
}

export type PageResponse = SettingsResponse & RecPageResponse;

export interface BehaviorSignalSettings {
  ElementBlocks?: BehaviorSignalElementBlock[];
}

export interface BehaviorSignalElementBlock {
  Selector: string;
  Signals: BehaviorSignalType[];
}
export namespace BehaviorSignalType {
  export const SignalInvalid = 0;
  export const SignalDeadClick = 1;
  export const SignalRageClick = 2;
}
export type BehaviorSignalType = (typeof BehaviorSignalType)[keyof typeof BehaviorSignalType];

export type PageFlags = {
  AjaxWatcher?: boolean;
  CanvasWatcherMode?: CanvasWatcherMode;
  CaptureAnimations?: boolean;
  ClientSideRageClick?: boolean;
  ConsoleWatcher?: boolean;
  DisableCopyPasteListener?: boolean;
  DisableImgUrlPrivacy?: boolean;
  EnableRecEvents?: boolean;
  FetchChildIntegrations?: boolean;
  FetchIntegrations?: boolean;
  FixWhenValues?: boolean;
  GetCurrentSession?: boolean;
  ResourceUploading?: boolean;
  UseClientSideId?: boolean;
  UseCompression?: boolean;
  UseDwellTime?: boolean;
  UseLZEncoding?: boolean;
  UseMinNetworkBudget?: boolean;
  UseRuleOptimization?: boolean;

  /** we don't use this in non-test contexts */
  DisableInertBundles?: boolean;
};

// keep in sync with recording.go
export namespace BlockReasonType {
  export const ReasonNoSuchOrg = 1;
  export const ReasonOrgDisabled = 2;
  export const ReasonOrgOverQuota = 3;
  export const ReasonBlockedDomain = 4;
  export const ReasonBlockedIp = 5;
  export const ReasonBlockedUserAgent = 6;
  export const ReasonBlockedGeo = 7;
  export const ReasonBlockedTrafficRamping = 8;
  export const ReasonInvalidURL = 9;
  export const ReasonUserOptOut = 10;
  export const ReasonInvalidRecScript = 11;
  export const ReasonDeletingUser = 12;
  export const ReasonNativeHookFailure = 13;
}
export type BlockReasonType = (typeof BlockReasonType)[keyof typeof BlockReasonType];

export interface PageErrorResponse {
  reason_code: BlockReasonType;
  cookie_domain?: string;
  user_id?: string;
}

// Constants used in ElementBlock.Type.
// Keep in sync with fs/model.BlockType.
export namespace BlockType {
  export const Unset = 0;
  export const Exclude = 1;
  export const Mask = 2;
  export const Unmask = 3;
  export const Watch = 4;
  export const Keep = 5;
  export const Defer = 6;
}
export type BlockType = (typeof BlockType)[keyof typeof BlockType];

export interface ElementBlock {
  Selector: string;
  Consent: boolean;
  Type: BlockType;
}

export interface ElementWatch {
  Selector: string;
}

export interface ElementDefer {
  Selector: string;
}

export type StorageKind = 'cookie' | 'localStorage';

// Constants used in ElementKeepType.
// Keep in sync with fs/model.KeepType.
export namespace KeepType {
  export const Unset = 0;
  export const Click = 1;
}
export type KeepType = (typeof KeepType)[keyof typeof KeepType];

export interface ElementKeep {
  Selector: string;
  Consent: boolean;
  Type: KeepType;
}

export interface UrlKeep {
  Regex: string;
  Consent: boolean;
}

export interface BundleResponse {
  BundleTime: TimeStampMs;
}

export interface EventBundle {
  When: TimeStampMs;
  Seq: number;
  Evts: RawEvent[];
}

// Resource hashing algorithms which are supported on the client side.
export type ResourceHashAlgorithm = 'fsnv' | 'sha1';

// A map from hash algorithms to a list of hashes computed using the
// corresponding algorithm.
export type ResourceMapping = { [algo in ResourceHashAlgorithm]?: string[] };

export interface QueryResourcesRequest {
  OrgId: string;
  HashesByAlgorithm: ResourceMapping;
}

export type QueryResourcesResponse = {
  QueryAlgorithm: string;
  QueryHash: string;
  Found: boolean;
  CanonicalHash?: {
    // Only fsnv hashes are currently canonical.
    Algorithm: ResourceHashAlgorithm & 'fsnv';
    Hash: string;
  };
}[];

// Reports the hash used to index the uploaded resource in GCS.
export type UploadResourceResponse = {
  Algorithm: ResourceHashAlgorithm;
  Hash: string;
};

///////////////////////////////////////////////////////////////////////////////
// WebSocket protocol types.
// NB: keep in sync with recsock.go
///////////////////////////////////////////////////////////////////////////////

/** WebSocket commands sent by the recording script. */
export enum SockRequestCommand {
  Page = 1,
  Bundle = 2,
  Event = 6,
  Settings = 8, // this cmd is 'fake' and not reserved in the server yet
}

/** A WebSocket request from the recording script to the server. */
export type SockRequest = {
  Seq: number;
} & SockRequestData;

export type SockRequestData =
  | {
      Cmd: SockRequestCommand.Page;
      Page: RecPageParams;
    }
  | {
      Cmd: SockRequestCommand.Bundle | SockRequestCommand.Event;
      Bundle: SockBundleReq;
    };

export interface SockBundleReq {
  Bundle: EventBundle;
  DeltaT?: number;
  Format?: string;
  OrgId: string;
  PageId: string;
  PageStart?: number;
  PrevBundleTime?: number;
  Seq: number;
  SessionId: string;
  UserId: string;
}

/** WebSocket commands included in responses from the server. */
export enum SockResponseCommand {
  Error = 3,
  Page = 4,
  Bundle = 5,
  Event = 7,
  Settings = 9, // this cmd is 'fake' and not reserved in the server yet
}

/** A WebSocket response from the server to the recording script. */
export type SockResponse = {
  Seq: number;
} & SockResponseData;

type SockResponseData =
  | {
      Cmd: SockResponseCommand.Error;
      Fail: SockFailure;
    }
  | {
      Cmd: SockResponseCommand.Page;
      Page: RecPageResponse;
    }
  | {
      Cmd: SockResponseCommand.Bundle | SockResponseCommand.Event;
      Bundle: BundleResponse;
    }
  | {
      Cmd: SockResponseCommand.Settings;
      Settings: SettingsResponse;
    };

export interface SockFailure {
  Status: number;
  Error: string;
}

///////////////////////////////////////////////////////////////////////////////
// Recording constants, possibly shared with UI code.
///////////////////////////////////////////////////////////////////////////////

export namespace Constants {
  // Max performance.mark|measure entries we ingest per page
  export const MaxPerfMarksPerPage = 16 * 1024;

  // Max console logs we ingest per page
  export const MaxLogsPerPage = 1024;

  // Max URL length we ingest
  export const MaxUrlLength = 2048;

  // The interval (in ms) at which all events will be processed including DOM
  // mutations.  This has user-visible effects, because it controls the
  // granularity at which we record dom state, endpoints for mouse/scroll
  // curves, and so forth.
  export const MutationProcessingInterval = 250;

  // The interval (in ms) at which all mouse/scroll events (or any queued event)
  // will be sampled.  This does NOT sample DOM mutations or DOM attribute sets.
  export const CurveSamplingInterval = 142; // 7Hz

  // The minimum interval (in ms) at which event bundles will be uploaded. This
  // can be overridden by the `BundleUploadInterval` field of the /rec/page
  // response.
  export const DefaultBundleUploadInterval = 5000;

  // Heartbeat intervals stop altogether after 30m.
  export const HeartbeatInterval = 4.27 * 60 * 1000; // 4.27 mins
  export const PageInactivityTimeout = 30 * 60 * 1000; // 30 mins

  // The max exponential backoff, in millis.
  export const BackoffMax = 5 * 60 * 1000;

  // Guarantee that the scroll sampling interval hits at least 4 times per event
  // window.
  export const ScrollSampleInterval = MutationProcessingInterval / 5;

  // Timeout for synthetic click event quiet period.
  export const SyntheticClickTimeout = ScrollSampleInterval + 5;

  // This should match the equivalent value in basicevents.go.
  export const InactivityThreshold = 4000;

  // Don't record more than 16k in each Ajax request/response payload.
  export const MaxAjaxPayloadLength = 16 * 1024;

  // default settings are maximally restrictive
  // recpage response will update these for each org
  export const DefaultOrgSettings: Readonly<OrgSettings> = {
    MaxPerfMarksPerPage,
    MaxConsoleLogPerPage: MaxLogsPerPage,
    MaxAjaxPayloadLength,
    MaxUrlLength,
    RecordPerformanceResourceImg: true,
    RecordPerformanceResourceTiming: true,
    HttpRequestHeadersAllowlist: [],
    HttpResponseHeadersAllowlist: [],
    UrlPrivacyConfig: [
      {
        Exclude: {
          Hash: [{ Expression: '#.*' }],
          QueryParam: [{ Expression: '(=)(.*)' }],
        },
      },
    ],
    // by default, if no org settings is detected, wipe every attribute
    AttributeBlocklist: [
      {
        Target: AttributeBlockTarget.Any,
        Tag: '*',
        Name: '',
        Type: AttributeBlockType.Prefix,
        Action: AttributeBlockAction.Erase,
      },
    ],
  };

  export const DefaultStatsSettings = {
    MaxPayloadLength: 8192, // 2 ** 13
    // NOTE: Max length is intended to prevent fraudulent data, not to limit the feature. -SN (1/4/22)
    MaxEventTypeLength: 1024,
  } as const;

  /**
   * A special value used to indicate that a field in an Ajax request or response
   * body was blocked.
   * TODO(sethf): It would be nice to update the value, as well as the name, to
   * use the modern "blocked" terminology.
   */
  export const BlockedFieldValue = '__fs__redacted';
  export const DefaultRecDisabledMessage =
    'Capture disabled. Turn on debug mode for more information.';
  export const ShutdownMessage = 'Shutdown called.';
  export const TextPlain = 'text/plain';
}

export type Constants = (typeof Constants)[keyof typeof Constants];

// @deprecated
export const DeprecatedSwanSongLocalStorageKey = '_fs_swan_song';
// @deprecated
export const LegacySwanSongLocalStorageKey = '_fs_song';
// The local storage key we use for SwanSong data.
export const SwanSongsLocalStorageKey = '_fs_songs';
// The local storage key we use for client-side Dwell bundles
export const FSDwellStorageKey = '_fs_bundle';
// fs user identity
export const UidLocalStorageKey = '_fs_uid';
// fs consent
export const CidLocalStorageKey = '_fs_cid';
// fs last user activity time
export const LuaLocalStorageKey = '_fs_lua';
// Dom listener override
export const TrustEventOverride = '_fs_trust_event';
export const FSMessageKey = '__fs';
export const CurrentScriptPolyfill = '_fs_currentScript';
export const FsNamespaceAttr = 'data-fs-namespace';
export const FsIsMobileAttr = 'data-fs-is-mobile';

export type FsBugsSeverity = 'error' | 'warning' | 'info';
export type FsBugsPayload = {
  projectRoot: string;
  deviceTime: number;
  inIframe: boolean;
  CompiledVersion: string;
  CompiledTimestamp: number;
  orgId: string | undefined;
  'userId:sessionId': string;
  context: string;
  message: any;
  severity: FsBugsSeverity;
  language: string;
  stacktrace: any;
};

export type BundleApiVersion = 'v1' | 'v2';

export type SchemaOptions = 'http:' | 'https:';

// HTTP payload types
export interface BlobBuilder {
  data: ArrayBuffer | string;
  contentType: string;
  filename?: string;
}

export interface StringBuilder {
  type: 'string';
  data: string;
}

export interface ArrayBufferBuilder {
  type: 'ArrayBuffer';
  data: ArrayBuffer;
  encoding: EncodingMode;
}

export interface FormDataBuilder {
  type: 'FormData';
  data: Record<string, BlobBuilder | string>;
}

export type PayloadBuilder = StringBuilder | ArrayBufferBuilder | FormDataBuilder;
export type Payload = string | ArrayBuffer | FormData;
export type BundleBuilder = ArrayBufferBuilder | StringBuilder;
export type BundleDescriptor = [seq: number, bundle: BundleBuilder, byteSize: number];
export const EncodingMode_Gzip = 'gzip';
export const EncodingMode_Identity = 'identity';
export type EncodingMode = typeof EncodingMode_Gzip | typeof EncodingMode_Identity;

export enum DwellState {
  Inactive = 1,
  Pending = 2,
  ShouldFlush = 3,
}
export interface StoredBundle {
  Bundles: BundleDescriptor[];
  /** Indicates this is the first page in the session */
  IsNewSession: boolean;
  /** Absolute time (ms) at which the last bundle was sent (for DeltaT calculation). */
  LastBundleTime?: number;
  OrgId: string;
  PageId: string;
  /** Absolute time (ms) at which the page was created (for debugging). */
  PageStartTime?: number;
  /** Swansongs after Aug 2022 contain the recording host to support multi-realm orgs */
  RecHost?: string;
  ServerBundleTime: number | null | undefined;
  /**
   * ServerPageStart and ServerBundleTime can be absent in songs that were persisted
   * in LocalStorage before the fields were added to the Song interface.
   */
  ServerPageStart: number | null | undefined;
  SessionId: string;
  UserId: string;
  Version: BundleApiVersion;

  // reserved: Encoding
}

export type StoredBundleOptions = {
  bundles: BundleDescriptor[];
  isNewSession: boolean;
  lastBundleTime: number;
  orgId: string;
  pageId: string;
  serverBundleTime: number | null | undefined;
  serverPageStart: number;
  sessionId: string;
  userId: string;
  version: BundleApiVersion;
};

export type BundleOptions = {
  bundle: BundleDescriptor;
  deltaT: number | null;
  isNewSession: boolean;
  lastUserActivity?: number;
  orgId: string;
  pageId: string;
  /** override for recording host: used for sending swansongs */
  recHost?: string;
  serverBundleTime: number | null | undefined;
  serverPageStart: number | null | undefined;
  sessionId: string;
  userId: string;
  /** Indicates which bundle endpoint to use /rec/bundle[/v2] - v2 expects GUID ids */
  version: BundleApiVersion;
  disableCompression?: boolean;
};

export type SettingsParams = {
  orgId: string;
  version?: 'v1';
  previewMode?: boolean;
};

export type StartBeaconParams = {
  orgId: string;
  userId: string;
  sessionId: string;
};

export type IFrameLike = HTMLIFrameElement | HTMLFrameElement;
export type FrameInjectorParameters = {
  appHost: string;
  desc: string;
  frame: IFrameLike;
  namespace: string;
  orgId: string;
  recHost: string;
  recSettingsHost: string;
  scheme: SchemaOptions;
  script?: string;
  snippetVersion: string;
  useNamespace?: boolean;

  // Optional parameters set only by Reftests
  underTest: boolean;
};

// Easy Bake
export type FSEventDetail = {
  eventStartTimeStamp: DOMHighResTimeStamp;
  eventEndTimeStamp?: DOMHighResTimeStamp;
  eventReplayUrlAtStart: string | null;
  eventReplayUrlAtCurrentTime: string | null;
};
export type FSEvent = CustomEvent<FSEventDetail> & {
  type: 'fullstory/rageclick';
};

// Conditional URL Privacy internal types
// Note - these are just regex versions of UrlPrivacyConfig types
export type Rule = RegExp;
export type RuleSet = {
  Hash: Rule[];
  Host: Rule[];
  Path: Rule[];
  QueryParam: Rule[];
  Query: Rule[];
};
export type RuleCondition = {
  If?: RuleSet[];
  Exclude: RuleSet;
};
export type RuleConfig = RuleCondition[];

export enum CaptureState {
  Shutdown = 1,
  Starting = 2,
  Started = 3,
  Fatal = 4,
}

// Compilation metadata. For TypeScript's purposes, we treat these as global
// variables. In reality, they're injected by joy, and the injected values
// are substituted directly at each reference to these variables, so at runtime
// these variables do not exist.
declare global {
  const CompiledTimestamp: number;
  const CompiledVersion: string;
}

export namespace CanvasOpType {
  export const Set = 0;
  export const Function = 1;
}
export type CanvasOpName = keyof typeof CanvasOpType;
export type CanvasOpType = (typeof CanvasOpType)[CanvasOpName];

/**
 * CanvasWatcherMode needs to match the values of recorder-canvas-watcher-mode
 */
export enum CanvasWatcherMode {
  Disabled = 0,
  /**
   * captures the operations done directly to the canvas and replay
   * advantages:
   * 1. for simpler operations this is smaller footprint
   * 2. allows potentially content masking
   * 3. all operations should be captured so nothing gets lost
   * disadvantages:
   * 1. operations done prior to initialization could be missed
   * 2. complex webgl operations do not work well, especially if
   *    script manipulates buffers, because each of those buffers must
   *    be encoded by us
   */
  CaptureCanvasOps = 1,

  /**
   * uses the screenshots of canvases and upload as screenshots
   * the advantages/disadvantages of this is almost entirely inverse of the above
   * advantages:
   * 1. for complex operations this is smaller footprint (esp webgl)
   * 2. captures current state of canvas, regardless of FS initialization
   * disadvantages:
   * 1. does not allow content masking (unless we redraw onto an in memory FS canvas)
   * 2. depends on the rate of refresh, could miss things drawn on canvas that goes away
   * 3. for simpler operation this is larger footprint
   */
  ScreenshotCanvas = 2,
}

// An object with similar properties to `URL`. This includes `URL` itself as
// well as `Location` and DOM elements that implement HTMLHyperlinkElementUtils.
export interface UrlLike {
  hash: string | null | undefined;
  host: string;
  hostname: string;
  href: string;
  pathname: string;
  port: string;
  protocol: string;
  search: string | null | undefined;
}

/** All supported message types */
export enum Messages {
  EndPreviewMode = 'EndPreviewMode',
  EvtBundle = 'EvtBundle',
  GreetFrame = 'GreetFrame',
  InitFrameMobile = 'InitFrameMobile',
  RequestFrameId = 'RequestFrameId',
  RestartFrame = 'RestartFrame',
  SetConsent = 'SetConsent',
  /**
   * Important note! Any changes here must be reflected in the `InitFrameMobile`, as there are
   * long-lived mobile clients in the field that expect the stable interface to work.
   */
  SetFrameId = 'SetFrameId',
  ShutdownFrame = 'ShutdownFrame',
  /** Unknown used as fallback, consider this a noop and internal only */
  Unknown = 'Unknown',
}

export type Payloads = {
  [Messages.EndPreviewMode]: [];
  [Messages.EvtBundle]: [bundle: RawEvent[], pageSignature?: string];
  [Messages.GreetFrame]: [frameId: number];
  [Messages.InitFrameMobile]: [
    jsonString: string,
    additionalData?: {
      ProtocolVersion: number;
      OuterStartTime: number;
      MobileScriptPath?: string; // optional value to specify url to script, similar to window._fs_script
    },
  ];
  [Messages.RequestFrameId]: [];
  [Messages.RestartFrame]: [forceRestart?: boolean];
  [Messages.SetConsent]: [nextConsent: ConsentState];
  [Messages.SetFrameId]: [
    frameId: number,
    parentIds: number[],
    startTime: number,
    scheme: string,
    script: string | undefined,
    appHost: string,
    orgId: string,
    initConfig: V2OperationOptions['init'] | undefined,
    pageRsp: PageResponse | undefined,
    consented: ConsentState,
    now: number,
  ];
  [Messages.ShutdownFrame]: [];
  [Messages.Unknown]: [];
};

/**
 * Message signatures used for communication between different instances of the
 * recording script running in different documents, or between the native mobile
 * recording code and the root document of a web view.
 */
export type Envelope<T extends Messages = Messages> = T extends Messages
  ? [type: T, ...data: Payloads[T]]
  : never;

/**
 * Cookie used to communicate between multiple tab that dwell time has passed
 */
export const DwellPassedCookieKey = '_fs_dwell_passed';

export const WayfinderNS = '__wayfinder';
export const WayfinderStyleAttr = '__wayfinder_style_v1';

export interface WayfinderStyleChangeEntry {
  kind:
    | typeof RawEventKind.CSSRULE_DELETE
    | typeof RawEventKind.CSSRULE_INSERT
    | typeof RawEventKind.CSSRULE_UPDATE;
  cssText?: string; // INSERT only: the text of the insert
  key?: string; // UPDATE only: the key that was updated
  value?: string | null; // UPDATE only: the value that was updated
  index: number;
  path?: number[];
}

export interface CompiledInformation {
  Ver: string;
  TS: number;
}

export interface RetrieveOptions<T> {
  keyMeta?: string;
  validate?: (item: T) => boolean;
}

export interface IMultiStorage<T> {
  store(obj: T, keyMeta?: string): boolean;
  retrieve(options?: RetrieveOptions<T>): T[];
}

export interface IWeakMap<K extends object, V> {
  delete(key: K): boolean;
  has(key: K): boolean;
  get(key: K): V | undefined;
  set(key: K, value: V): this;
}

// synced with fullstory/nativemobile/shared/shared-core/src/webview/tracker.rs
export enum WebViewMessageType {
  LoadEvent = 0,
  MessageEvent = 1,
  UnloadEvent = 2,
  ApiEvent = 3,
  AllowlistCheck = 4,
}
